import React, { useCallback, useRef, useState } from "react";
import { Switch } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";

import { TStore } from "models";
import { getMembers, setEditProfile, updateProfileAccountSettings } from "store";
import styles from "./useProfileGate.module.scss";

interface ProfileTarget {
	id: number;
	memberDirectoryOptIn?: boolean;
	profileVisible?: boolean;
}

const stateSelector = createSelector(
	(state: TStore) => state.editProfile?.data?.user,
	(state: TStore) => state.auth?.credentials?.user?.id,
	(editProfileUser, currentUserId) => ({
		viewerOptedIn: !!editProfileUser?.member_directory_opt_in,
		currentUserId,
	}),
);

// Treat undefined as opted in (graceful fallback when API doesn't return field).
const targetIsPrivate = (t: ProfileTarget) =>
	t.memberDirectoryOptIn === false || t.profileVisible === false;

export function useProfileGate({
	onBeforeNavigate,
}: { onBeforeNavigate?: () => void } = {}) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { viewerOptedIn, currentUserId } = useSelector(stateSelector);
	const [modal, setModal] = useState<"none" | "optIn" | "private">("none");
	const pendingTargetId = useRef<number | null>(null);

	const navigateToProfile = useCallback(
		(id: number) => {
			onBeforeNavigate?.();
			navigate("/members/" + id);
		},
		[navigate, onBeforeNavigate],
	);

	const open = useCallback(
		(target: ProfileTarget) => {
			// Self → navigate, no checks
			// eslint-disable-next-line eqeqeq
			if (currentUserId != null && target.id == currentUserId) {
				navigateToProfile(target.id);
				return;
			}
			// Target privacy first — if private, opting in won't help, so show that.
			if (targetIsPrivate(target)) {
				setModal("private");
				return;
			}
			// Viewer opted out + target public → invite to opt in
			if (!viewerOptedIn) {
				pendingTargetId.current = target.id;
				setModal("optIn");
				return;
			}
			// Both opted in → navigate
			navigateToProfile(target.id);
		},
		[viewerOptedIn, currentUserId, navigateToProfile],
	);

	const close = useCallback(() => {
		setModal("none");
		pendingTargetId.current = null;
	}, []);

	const handleOptInToggle = useCallback(
		(checked: boolean) => {
			if (!checked) return; // only act on turning ON
			const targetId = pendingTargetId.current;
			dispatch(
				updateProfileAccountSettings({
					userData: { member_directory_opt_in: true },
					callbackOnSuccess: (updatedUserData: any) => {
						dispatch(setEditProfile({ user: updatedUserData.data.user }));
						dispatch(getMembers());
						setModal("none");
						if (targetId != null) {
							navigateToProfile(targetId);
						}
						pendingTargetId.current = null;
					},
				}),
			);
		},
		[dispatch, navigateToProfile],
	);

	const node = (
		<>
			{modal === "optIn" && (
				<div className={styles.overlay} onClick={close}>
					<div
						className={styles.modal}
						onClick={(e) => e.stopPropagation()}
					>
						<button
							className={styles.closeBtn}
							onClick={close}
							aria-label="Close"
							type="button"
						>
							&#10005;
						</button>
						<h3 className={styles.title}>Would you like to opt in?</h3>
						<p className={styles.body}>
							In order to see and contact the other members of the
							community, you must opt in to be a part of the directory.
						</p>
						<div className={styles.switchRow}>
							<span className={styles.switchLabel}>DIRECTORY OPT-IN</span>
							<Switch
								className={styles.themedSwitch}
								checked={false}
								onChange={handleOptInToggle}
							/>
						</div>
					</div>
				</div>
			)}
			{modal === "private" && (
				<div className={styles.overlay} onClick={close}>
					<div
						className={styles.modal}
						onClick={(e) => e.stopPropagation()}
					>
						<button
							className={styles.closeBtn}
							onClick={close}
							aria-label="Close"
							type="button"
						>
							&#10005;
						</button>
						<h3 className={styles.title}>Member is Private</h3>
						<p className={styles.body}>
							This member has opted out of the directory, so their profile
							isn't viewable. They've chosen to keep their information
							private.
						</p>
						<button
							type="button"
							className={styles.okBtn}
							onClick={close}
						>
							OK
						</button>
					</div>
				</div>
			)}
		</>
	);

	return { open, node };
}
